<template>
    <div
        class="legacy-content-items"
    >
        <DebugContent
            title="DEBUG: Legacy Content Items"
            class="ma-8"
        >
            <template #expand>
                <ul>
                    <li v-for="(item, index) in contentTypes" :key="index">
                        <span>{{ item }}</span>
                    </li>
                </ul>
            </template>
        </DebugContent>

        <template
            v-for="(group, groupIndex) in contentGroups"
            :key="`content-group-${groupIndex}`"
        >
            <ContentBlocks
                v-if="group.kind === 'legacy'"
                :blocks="group.blocks"
                class="d-flex fill-width fill-height"
            ></ContentBlocks>
            <template
                v-else
            >
                <BlockRenderer
                    v-for="(block, blockIndex) in group.blocks"
                    :key="`content-group-${groupIndex}-block-${blockIndex}`"
                    :model-value="block"
                ></BlockRenderer>
            </template>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import type { LegacyContentItem, ContentBlock, ContentBlockRendered } from '~/types';
    import { LayoutTypeForFeatures } from '~/types';
    import { useLegacyContent } from '~/composables';
    import {
        useCdnResource
    } from 'x1-base/composables';

    const ContentBlocks = defineAsyncComponent(() => import('~/components/content/ContentBlocks/ContentBlocks.vue'));
    const BlockRenderer = defineAsyncComponent(() => import('~/components/content/blocks/v2/BlockRenderer/BlockRenderer.vue'));
    const DebugContent = defineAsyncComponent(() => import('~/components/DebugContent/DebugContent.vue'));

    type MergedContentEntry =
        | { kind: 'legacy'; sortOrder: number; item: LegacyContentItem }
        | { kind: 'rendered'; sortOrder: number; block: ContentBlockRendered };

    type ContentGroup =
        | { kind: 'legacy'; blocks: ContentBlock[] }
        | { kind: 'rendered'; blocks: ContentBlockRendered[] };

    const props = defineProps<{
        items: LegacyContentItem[];
        contentBlocks?: ContentBlockRendered[];
    }>();

    const { cdnBasePathSkCms } = useCdnResource();
    const { getFeatureBlockObj } = useLegacyContent({
        cdnBasePath: cdnBasePathSkCms
    });
    const contentTypes = ref<string[]>([]);
    const contentGroups = ref<ContentGroup[]>([]);

    function convertLegacyItems(items: LegacyContentItem[]): ContentBlock[] {
        const blocks: ContentBlock[] = [];

        function createHalf(index: number, item: LegacyContentItem, isFirst: boolean, isLast: boolean): ContentBlock | null {
            const prevItem = items[index - 1];
            const nextItem = items[index + 1];

            const isFirstInRow = (prevItem && prevItem.width !== 'half') ?? false;
            const isLastInRow = (nextItem && nextItem.width !== 'half') ?? false;

            contentTypes.value.push(`(${item.id}) HALF: Half Width / Text w/Background (FIRST: ${isFirstInRow} | LAST: ${isLastInRow})`);
            return getFeatureBlockObj(LayoutTypeForFeatures.HALF, item, isFirst, isLast, isFirstInRow, isLastInRow);
        }

        function createHorizontal(index: number, item: LegacyContentItem, isFirst: boolean, isLast: boolean): ContentBlock | null {
            contentTypes.value.push(`(${item.id}) HORIZONTAL: Full Width / Text & Image / Side-by-Side`);
            return getFeatureBlockObj(LayoutTypeForFeatures.HORIZONTAL, item, isFirst, isLast);
        }

        function createVertical(index: number, item: LegacyContentItem, isFirst: boolean, isLast: boolean): ContentBlock | null {
            contentTypes.value.push(`(${item.id}) VERTICAL: Full Width / Text & Image / Top-to-Bottom`);
            return getFeatureBlockObj(LayoutTypeForFeatures.VERTICAL, item, isFirst, isLast);
        }

        function createStacked(index: number, item: LegacyContentItem, isFirst: boolean, isLast: boolean): ContentBlock | null {
            contentTypes.value.push(`(${item.id}) STACKED: Full Width / Text Absolute Positioned w/Background`);
            return getFeatureBlockObj(LayoutTypeForFeatures.STACKED, item, isFirst, isLast);
        }

        items.forEach((item, index) => {
            let block: ContentBlock | null = null;
            const isFirst = index === 0;
            const isLast = index === items.length - 1;

            // First Try to use the new layoutType property if it exists
            if (item.layoutType) {
                switch (item.layoutType) {
                    case LayoutTypeForFeatures.HALF:
                        block = createHalf(index, item, isFirst, isLast);
                        break;
                    case LayoutTypeForFeatures.HORIZONTAL:
                        block = createHorizontal(index, item, isFirst, isLast);
                        break;
                    case LayoutTypeForFeatures.VERTICAL:
                        block = createVertical(index, item, isFirst, isLast);
                        break;
                    case LayoutTypeForFeatures.STACKED:
                        block = createStacked(index, item, isFirst, isLast);
                        break;
                }
            } else {
                // Fallback to type detection
                if (item.width === 'half') {
                    // Half Width / Text w/Background
                    block = createHalf(index, item, isFirst, isLast);
                } else if (item.textPosX.match(/left|right/) && item.imagePosX.match(/left|right/)) {
                    // Full Width / Text & Image / Side-by-Side
                    block = createHorizontal(index, item, isFirst, isLast);
                } else if (
                    (item.textPosY === 'top' &&
                        (item.imagePosY === 'middle' || item.imagePosY === 'bottom') &&
                        item.imagePosX !== 'center') ||
                    (item.imagePosY === 'top' &&
                        (item.textPosY === 'middle' || item.textPosY === 'bottom') &&
                        item.imagePosX !== 'center')
                ) {
                    // Full Width / Text & Image / Top-to-Bottom
                    block = createVertical(index, item, isFirst, isLast);
                } else {
                    // item.layoutType === 'stacked'
                    // Full Width / Text Absolute Positioned w/Background
                    block = createStacked(index, item, isFirst, isLast);
                }
            }

            if (block) {
                blocks.push(block);
            }
        });

        return blocks;
    }

    const mergedEntries: MergedContentEntry[] = [
        ...(props.items ?? []).map((item) => ({ kind: 'legacy' as const, sortOrder: item.sortOrder, item })),
        ...(props.contentBlocks ?? []).map((block) => ({ kind: 'rendered' as const, sortOrder: block.sortOrder, block }))
    ].sort((a, b) => a.sortOrder - b.sortOrder);

    // Group consecutive same-kind entries so runs of legacy items keep their
    // row-aware conversion (half-width pairing) and share one ContentBlocks wrapper.
    let legacyRun: LegacyContentItem[] = [];

    function flushLegacyRun(): void {
        if (legacyRun.length) {
            contentGroups.value.push({ kind: 'legacy', blocks: convertLegacyItems(legacyRun) });
            legacyRun = [];
        }
    }

    mergedEntries.forEach((entry) => {
        if (entry.kind === 'legacy') {
            legacyRun.push(entry.item);
        } else {
            flushLegacyRun();
            const lastGroup = contentGroups.value[contentGroups.value.length - 1];

            if (lastGroup?.kind === 'rendered') {
                lastGroup.blocks.push(entry.block);
            } else {
                contentGroups.value.push({ kind: 'rendered', blocks: [entry.block] });
            }
        }
    });
    flushLegacyRun();
</script>

<style lang="scss" scoped></style>
